import { boolean, schema, string } from "@anna-money/anna-web-lib/serializer";
import { Config } from "config/config";
import type { AnalyticsManager } from "services/analytics/analyticsManager";
import { DataStorage } from "services/storage/dataStorage";
import { AuthClient } from "./authClient";
import { UnauthenticatedError } from "./errors";

const accessTokenKey = "access_token";
const accessTokenRefreshed = "access_token_refreshed";

export class AccessTokenPayload extends schema(
  {
    data: string(),
  },
  "AccessTokenPayload",
) {}

class AccessTokenRefreshedPayload extends schema(
  {
    isRefreshed: boolean(),
  },
  "AccessTokenRefreshedPayload",
) {}

interface AuthUrlParams {
  state: string;
  loginHint?: string;
}

export class Authenticator {
  private readonly _config: Config;
  private readonly _authClient: AuthClient;
  private readonly _dataStorage: DataStorage;
  private readonly _analyticsManager: AnalyticsManager;

  constructor(config: Config, authClient: AuthClient, dataStorage: DataStorage, analyticsManager: AnalyticsManager) {
    this._config = config;
    this._authClient = authClient;
    this._dataStorage = dataStorage;
    this._analyticsManager = analyticsManager;
  }

  get isAuthenticated(): boolean {
    return !!this._loadAccessToken();
  }

  get isTokenRefreshed(): boolean {
    this._analyticsManager.event("authenticator.check-token-refreshed", {
      isRefreshed: Boolean(this._dataStorage.get(accessTokenRefreshed, AccessTokenRefreshedPayload)?.isRefreshed),
    });
    return Boolean(this._dataStorage.get(accessTokenRefreshed, AccessTokenRefreshedPayload)?.isRefreshed);
  }

  get accessToken(): string {
    const accessToken = this._loadAccessToken();
    if (!accessToken) {
      throw new UnauthenticatedError();
    }
    return accessToken.data;
  }

  getCompanyId(): string | undefined {
    const accessToken = this._loadAccessToken();
    if (!accessToken) {
      return undefined;
    }
    this._analyticsManager.event("authenticator.check-company-id", {
      companyId: this._extractCompanyIdFromJWT(accessToken.data),
    });
    const companyId = this._extractCompanyIdFromJWT(accessToken.data);
    return companyId !== null && companyId !== "null" && companyId !== undefined && companyId !== "undefined"
      ? companyId
      : undefined;
  }

  getAuthUrl(callbackUrl: URL, { state, loginHint }: AuthUrlParams): string {
    const params = new URLSearchParams({
      client_id: this._config.annaAuthClientId,
      redirect_uri: callbackUrl.toString(),
      screen: "signup",
      state,
    });
    if (loginHint) {
      params.append("login_hint", loginHint);
    }
    return `${this._config.annaAuthUrl}/authorize?${params.toString()}`;
  }

  getLogoutRedirectUrl(): string {
    return "/";
  }

  async authenticate(currentUrl: URL, callbackUrl: URL): Promise<boolean> {
    const authCode = currentUrl.searchParams.get("code");
    if (!authCode) {
      return false;
    }

    const accessToken = await this._authClient.getAccessToken(
      this._config.annaAuthClientId,
      authCode,
      callbackUrl.toString(),
    );
    this._saveAccessToken(new AccessTokenPayload({ data: accessToken }));
    return true;
  }

  reset(): void {
    this._dataStorage.remove(accessTokenKey);
  }

  markTokenRefreshed(): void {
    this._analyticsManager.event("authenticator.mark-token-refreshed", {
      isRefreshed: Boolean(this._dataStorage.get(accessTokenRefreshed, AccessTokenRefreshedPayload)?.isRefreshed),
    });
    this._dataStorage.set(accessTokenRefreshed, new AccessTokenRefreshedPayload({ isRefreshed: true }));
  }

  initTokenRefreshedFlag(): void {
    this._dataStorage.set(accessTokenRefreshed, new AccessTokenRefreshedPayload({ isRefreshed: false }));
  }

  async logout(): Promise<void> {
    this._dataStorage.remove(accessTokenKey);
    this._dataStorage.remove(accessTokenRefreshed);
    await this._authClient.logout();
  }

  private _saveAccessToken(accessToken: AccessTokenPayload): void {
    const companyId = this._extractCompanyIdFromJWT(accessToken.data);
    this._analyticsManager.event("access-token.saved", { companyId });
    this._dataStorage.set(accessTokenKey, accessToken);
  }

  private _extractCompanyIdFromJWT(token: string): string {
    try {
      const parts = token.split(".");

      if (parts.length !== 3 || !parts[1]) {
        console.error("Invalid JWT token format: expected 3 parts");
        return "unknown";
      }

      const payload = JSON.parse(atob(parts[1]));

      return payload.company_id;
    } catch (error) {
      console.error("Failed to extract company_id from JWT token:", error);
      return "unknown";
    }
  }

  private _loadAccessToken(): AccessTokenPayload | null {
    return this._dataStorage.get(accessTokenKey, AccessTokenPayload);
  }
}
